import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Field, FieldProps, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { Button, Container } from 'components'
import Header from './Header/Header'
import { AppliesTo, Conditions, PromoCodeStatus } from 'api/promoCodes/enums'
import { createPromoCode, formatPromoCodePayload } from 'api/promoCodes/promoCodes'
import { PromoCodeFormValues, couponValues } from 'api/promoCodes/types'
import { PromoCodeRules } from 'api/models'
import { useGetOrganizations, useIsMobile } from 'hooks'
import { getPromoCodesURL } from 'utils'
import * as Styled from './styles'


const validationSchema = Yup.object({
  code: Yup.string()
    .required('Coupon code is required')
    .min(6, 'Coupon code must be at least 6 characters'),
  discount_type: Yup.string().required('Coupon type is required'),
  discount_amount: Yup.number()
    .when('discount_type', {
      is: couponValues.PERCENTAGE_OFF.value,
      then: Yup.number()
        .min(1, 'Percentage must be at least 1%')
        .max(100, 'Percentage cannot exceed 100%')
        .typeError('Discount must be a number between 1 and 100'),
      otherwise: Yup.number()
        .positive('Discount must be a positive number')
        .min(0.1, 'Discount must be at least $0.10')
        .typeError('Discount must be a number')
        .test(
          'max-two-decimals',
          'Discount can have at most two decimal places',
          value => value === undefined || /^\d+(\.\d{1,2})?$/.test(String(value))
        ),
  }),
  starts_at: Yup.date()
    .when('includeEndDate', {
      is: true,
      then: Yup.date()
        .nullable(),
      otherwise: Yup.date().nullable(),
    }),
  expires_at: Yup.date()
    .nullable()
    .when('includeEndDate', {
      is: true,
      then: Yup.date()
        .required('End date is required')
        .test(
          'same-or-after-start',
          'End date cannot be before start date',
          function (value) {
            const { starts_at } = this.parent
            if (!value || !starts_at) return true

            const start = new Date(starts_at).setHours(0, 0, 0, 0)
            const end = new Date(value).setHours(0, 0, 0, 0)

            return end >= start
          }
        ),
      otherwise: Yup.date().nullable().notRequired(),
  }),
  order_minimum: Yup.number()
    .when('conditions', {
      is: Conditions.MINIMUM_PURCHASE_AMOUNT,
      then: Yup.number()
        .required('Minimum purchase amount is required')
        .positive('Minimum amount must be a positive number')
        .typeError('Minimum amount must be a number')
        .min(0.1, 'Minimum purchase amount must be at least $0.10')
        .test(
          'max-two-decimals',
          'Amount can have at most two decimal places',
          value => value === undefined || /^\d+(\.\d{1,2})?$/.test(String(value))
        ),
      otherwise: Yup.number().nullable().notRequired(),
    }),
})

const PromoCodesForm = () => {
  const navigate = useNavigate()
  const { isMobile } = useIsMobile()
  const { defaultOrganization } = useGetOrganizations()
    
  const handleSubmit = async (values: PromoCodeFormValues) => {
    const {
      includeEndDate,
      order_minimum,
      conditions,
      applies_to,
      ...payload
    } = values

    if (!defaultOrganization?.id) {
      toast.error('You must select an organization before creating a promo code.')
      return
    }
    
    try {
      const organizationId = String(defaultOrganization.id)
      const rules: PromoCodeRules = {}

      if (conditions === Conditions.MINIMUM_PURCHASE_AMOUNT && order_minimum) {
        rules.order_minimum = String(order_minimum)
      }

      if (conditions === Conditions.SINGLE_USAGE_PER_CUSTOMER) {
        rules.one_time_use = true
      }

      const payloadInput = formatPromoCodePayload({
        ...payload,
        rules,
      })

      await createPromoCode(organizationId, payloadInput)
      toast.success('Promo code successfully created.', { autoClose: 5000 })
      navigate(getPromoCodesURL(organizationId))
    } catch (error: any) {
      // Check if the error has a specific response structure
      if (error?.response?.data?.errors) {
        const errorMessage = error.response.data.errors[0]?.message || 'Unknown error occurred'
        toast.error(errorMessage, { autoClose: 5000 })
      } else {
        toast.error(`Unknown error: ${String(error)}`, { autoClose: 5000 })
      }
    }
  }

  const appliesToOptions = Object.values(AppliesTo)
  const conditionsOptions = Object.values(Conditions)

  return (
    <>
      <Header title='Create Coupon' />
      <Container>
        <Formik
          initialValues={{
            code: '',
            discount_type: couponValues.FIXED_DISCOUNT.value,
            discount_amount: 0,
            applies_to: AppliesTo.ENTIRE_SALE,
            conditions: Conditions.MINIMUM_PURCHASE_AMOUNT,
            order_minimum: null,
            starts_at: new Date(),
            expires_at: null,
            aasm_state: PromoCodeStatus.ACTIVE,
            includeEndDate: true,
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          enableReinitialize={true}
        >
          {({ values, setFieldValue, isValid, touched, errors }) => (
            <Form>
              <Styled.FormContainer>
                <Styled.Row style={{ marginBottom: 20 }}>
                  <Styled.PromoCodesTitle>Coupon Configuration</Styled.PromoCodesTitle>
                </Styled.Row>
                <Styled.Row>
                  <Styled.FormLabel>Coupon Name</Styled.FormLabel>
                </Styled.Row>
                <Styled.Row>
                  <Field
                    name='code'
                    type='text'
                    width={isMobile ? '100%' : '170px'}
                    required
                    as={Styled.Input}
                  />
                  {touched.code && errors.code && (
                    <Styled.ErrorText>{errors.code}</Styled.ErrorText>
                  )}
                </Styled.Row>
                <Styled.Row>
                  <Styled.FormLabel>Coupon Type</Styled.FormLabel>
                </Styled.Row>
                <Styled.Row>
                  <Styled.InlineRadioWrapper>
                    {Object.keys(couponValues).map((key) => {
                      const couponType = key as keyof typeof couponValues
                      return (
                        <Styled.InlineFormControlLabel
                          key={couponType}
                          value={couponValues[couponType].value}
                          control={
                            <Styled.CustomRadio
                              checked={values.discount_type === couponValues[couponType].value}
                              onChange={(e) => {
                                setFieldValue('discount_amount', 0)
                                setFieldValue('discount_type', e.target.value)
                              }}
                            />
                          }
                          label={couponValues[couponType].label}
                          labelPlacement='end'
                        />
                      )
                    })}
                  </Styled.InlineRadioWrapper>
                </Styled.Row>
                <Styled.Row>
                  <Field
                    name='discount_amount'
                    required
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = e.target
                      setFieldValue('discount_amount', value ? Number(value) : null)
                    }}
                    as={Styled.Input}
                    >
                      {({ field }: FieldProps<any>) => (
                        <div style={{ position: 'relative', width: isMobile ? '100%' : '170px' }}>
                          <Styled.Input
                            {...field}
                            type='number'
                            autoComplete='off'
                            min={values.discount_type === couponValues.PERCENTAGE_OFF.value ? 1 : undefined}
                            max={values.discount_type === couponValues.PERCENTAGE_OFF.value ? 100 : undefined}
                            step={values.discount_type === couponValues.PERCENTAGE_OFF.value ? 1 : 0.01}
                            style={{ paddingLeft: '25px' }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              left: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                            }}
                          >
                            {values.discount_type === couponValues.PERCENTAGE_OFF.value ? '%' : '$'}
                          </span>
                        </div>
                      )}
                    </Field>
                  {touched.discount_amount && errors.discount_amount && (
                    <Styled.ErrorText>{errors.discount_amount}</Styled.ErrorText>
                  )}
                </Styled.Row>
                <Styled.Row>
                  <Styled.FormLabel>Applies To</Styled.FormLabel>
                </Styled.Row>
                <Styled.Row>
                  <Styled.InlineRadioWrapper>
                    {appliesToOptions.map((type) => (
                      <Styled.InlineFormControlLabel
                        key={type}
                        value={type}
                        control={
                          <Styled.CustomRadio
                            checked={values.applies_to === type}
                            onChange={(e) => setFieldValue('applies_to', e.target.value)}
                          />
                        }
                        label={type}
                        labelPlacement='end'
                      />
                    ))}
                  </Styled.InlineRadioWrapper>
                </Styled.Row>
                <Styled.Row>
                  <Styled.FormLabel>Conditions</Styled.FormLabel>
                </Styled.Row>
                <Styled.Row>
                  <Styled.ColumnCheckboxWrapper>
                  {conditionsOptions.map((condition) => (
                    <div key={condition} style={{ display: 'flex', alignItems: 'center' }}>
                      <Styled.InlineFormControlLabel
                        value={condition}
                        control={
                          <Styled.CustomCheckbox
                            checked={values.conditions === condition}
                            onChange={(e) =>
                              setFieldValue('conditions', e.target.checked ? condition : '')
                            }
                          />
                        }
                        label={condition}
                        labelPlacement='end'
                      />
                      {condition === Conditions.SINGLE_USAGE_PER_CUSTOMER && (
                        <Tooltip
                          title='This Promo Code can only be used once per phone number.'
                          arrow
                        >
                          <InfoOutlinedIcon
                            style={{
                              marginLeft: '8px',
                              color: '#777777',
                              fontSize: '20px',
                              cursor: 'pointer',
                            }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  ))}
                  </Styled.ColumnCheckboxWrapper>
                </Styled.Row>
              {values.conditions === Conditions.MINIMUM_PURCHASE_AMOUNT && (
                <>
                  <Styled.Row>
                    <Field name='order_minimum'>
                      {({ field }: FieldProps<any>) => (
                        <div style={{ position: 'relative', width: isMobile ? '100%' : '170px' }}>
                          <Styled.Input
                            {...field}
                            type='number'
                            step='0.01'
                            autoComplete='off'
                            placeholder='Minimum purchase amount'
                            style={{ paddingLeft: '25px' }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              left: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#555',
                            }}
                          >
                            $
                          </span>
                        </div>
                      )}
                    </Field>
                    {touched.order_minimum && errors.order_minimum && (
                      <Styled.ErrorText>{errors.order_minimum}</Styled.ErrorText>
                    )}
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Subtext>
                      This condition must be applied to the entire sale. If you've applied it to a
                      specific category or item, please adjust the selection accordingly.
                    </Styled.Subtext>
                  </Styled.Row>
                </>
              )}  
                <Styled.Row
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Styled.ActiveDatesText>Active Dates</Styled.ActiveDatesText>
                  <Styled.ColumnCheckboxWrapper>
                    <Styled.InlineFormControlLabel
                      control={
                        <Styled.CustomCheckbox
                          checked={values.includeEndDate}
                          onChange={() => {
                            const newValue = !values.includeEndDate
                            if (!newValue) {
                              setFieldValue('expires_at', null)
                            }
                            setFieldValue('includeEndDate', newValue)
                          }}
                        />
                      }
                      label={'Set end date'}
                      labelPlacement='end'
                    />
                  </Styled.ColumnCheckboxWrapper>
                </Styled.Row>
                <Styled.Row style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Styled.FormLabel style={{ flex: 1, textAlign: 'left' }}>
                    Start Date
                  </Styled.FormLabel>
                  {values.includeEndDate && (
                    <Styled.FormLabel style={{ flex: 1, textAlign: 'left' }}>
                      End Date
                    </Styled.FormLabel>
                  )}
                </Styled.Row>
                <Styled.Row style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 8 }}>
                  <Styled.DatePickerWrapper data-testid='date-picker-wrapper'>
                    <Styled.DateWrapper>
                      <Styled.DateInput
                        selected={values.starts_at ? values.starts_at : new Date()}
                        onChange={(date: Date) => setFieldValue('starts_at', date)}
                        dateFormat='MM/dd/yyyy'
                        placeholderText='Select a date'
                        popperPlacement='right-start'
                        showIcon
                        required
                        minDate={new Date()}
                        error={touched.starts_at && errors.starts_at ? true : false}
                      />
                    </Styled.DateWrapper>
                  </Styled.DatePickerWrapper>

                  {values.includeEndDate && (
                    <Styled.DatePickerWrapper data-testid='date-picker-wrapper'>
                      <Styled.DateWrapper>
                        <Styled.DateInput
                          selected={values.expires_at}
                          onChange={(date: Date) => setFieldValue('expires_at', date)}
                          dateFormat='MM/dd/yyyy'
                          placeholderText='Select a date'
                          popperPlacement='right-start'
                          showIcon
                          required={values.includeEndDate}
                          minDate={new Date()}
                          error={touched.expires_at && errors.expires_at ? true : false}
                        />
                      </Styled.DateWrapper>
                    </Styled.DatePickerWrapper>
                  )}
                </Styled.Row>
              </Styled.FormContainer>
              <Styled.Row full style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8 }}>
                <Button onClick={() => navigate(getPromoCodesURL())} variant='textonly' narrow>
                  Discard
                </Button>
                <Button type='submit' narrow disabled={!isValid}>
                  Create Coupon
                </Button>
              </Styled.Row>
            </Form>
          )}
        </Formik>
      </Container>
    </>
  )
}

export default PromoCodesForm
